import { useTheme } from './components/Theme/ThemeProvider';
import { Menu } from './components/Menu/Menu';
import { MobileMenu } from './components/Menu/MobileMenu';
import { Header } from './components/Header/Header';
import { MainContent } from './components/MainContent/MainContent';
import ChatPage from './pages/ChatPage';
import { DisclaimerNote } from './components/DisclaimerNote/DisclaimerNote';
import { ImageModal } from './components/Dialogs/ImageModal';
import { JiraCardModal } from './components/Dialogs/JiraCardModal';
import { WelcomePopup } from './components/Tutorial/WelcomePopup';
import { GuidedTutorial } from './components/Tutorial/GuidedTutorial';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearUploadedFiles, clearAnalysisResults, ensureSessionId, resetSessionId, clearTranslationData } from './store/chatSlice';
import { clearAutoDownloadedFiles } from './components/Chat/MessageContentRenderer';
import { setVoiceModeActive, setVoiceModeListening, setVoiceModePaused, setVoiceModeRestarting, setShowInactivityWarning, setInactivityCountdown, setDisconnectedByInactivity } from './store/voiceModeSlice';
import { voiceModeService } from './utils/voiceMode.service';
import logger from './utils/logger';

import { 
  initializeTutorial, 
  startTutorial, 
  skipTutorial, 
  nextStep, 
  previousStep, 
  exitTutorial, 
  showWelcome 
} from './store/tutorialSlice';
import { hasSeenTutorial, markTutorialAsCompleted } from './utils/tutorialStorage';
import { GalleryMode } from './components/Gallery/data/galleryTypes';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import './App.css';
import type { RootState } from './store/store';
import { cancelTranscribingMessages, clearaskChatHistory, clearRagHistory, setRagChatType, type ChatModeState, type RagChatType } from './store/chatModeSlice';

function App() {
  const { mode } = useTheme();
  const dispatch = useDispatch();
  const chatModeState: ChatModeState = useSelector((state: RootState) => state.chatMode);
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const { isActive: isVoiceModeActive } = voiceModeState;
  
  // Initialize session ID when app loads (ensures unique session per tab)
  useEffect(() => {
    dispatch(ensureSessionId());
  }, [dispatch]);

  // Tutorial state from Redux
  const tutorialState = useSelector((state: RootState) => state.tutorial);
  const { isActive: isTutorialActive, currentStepIndex, steps, showWelcomePopup } = tutorialState;
  const currentTutorialStep = steps[currentStepIndex];
  
  // Initialize tutorial on mount
  useEffect(() => {
    const hasCompleted = hasSeenTutorial();
    dispatch(initializeTutorial(hasCompleted));
  }, [dispatch]);
  
  // Save tutorial completion to localStorage when user completes or skips
  useEffect(() => {
    if (tutorialState.hasSeenTutorial) {
      markTutorialAsCompleted();
    }
  }, [tutorialState.hasSeenTutorial]);
  
  // Tutorial handlers
  const handleStartTutorial = useCallback(() => {
    dispatch(startTutorial());
  }, [dispatch]);
  
  const handleSkipTutorial = useCallback(() => {
    dispatch(skipTutorial());
    markTutorialAsCompleted(); // Immediately save to localStorage
  }, [dispatch]);
  
  const handleNextStep = useCallback(() => {
    // Check if we're on the last step - if so, mark as completed in localStorage
    if (currentStepIndex === steps.length - 1) {
      markTutorialAsCompleted();
    }
    dispatch(nextStep());
  }, [dispatch, currentStepIndex, steps.length]);
  
  const handlePreviousStep = useCallback(() => {
    dispatch(previousStep());
  }, [dispatch]);
  
  const handleExitTutorial = useCallback(() => {
    dispatch(exitTutorial());
    markTutorialAsCompleted(); // Immediately save to localStorage
  }, [dispatch]);
  
  const handleShowWhatsNew = useCallback(() => {
    dispatch(showWelcome());
  }, [dispatch]);
  
  // Page routing state
  const [currentPage, setCurrentPage] = useState<'main' | 'chat'>('main');
  const [chatMessage, setChatMessage] = useState<string>('');
  const [chatTools, setChatTools] = useState<string[]>([]);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Device detection using custom hook
  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet, windowWidth } = deviceInfo;
  
  // Log device info for debugging (remove in production)
  useEffect(() => {
    logger.log('Device Info:', deviceInfo);
  }, [deviceInfo]);

  // Determine if MainContent should be hidden
  const shouldHideMainContent = windowWidth < 600 && !isMobile && !isTablet;

  // Menu width state for responsive disclaimer positioning (persisted across reloads)
  const [menuWidth, setMenuWidth] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('menu-width');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) return parsed; // allow 0 (collapsed) or any valid stored width
      }
    }
    return 90; // medium default
  });

  // Keep localStorage in sync if external code adjusts menuWidth directly (non-drag scenarios)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('menu-width');
      const parsed = saved ? parseInt(saved, 10) : null;
      if (parsed !== menuWidth) {
        localStorage.setItem('menu-width', menuWidth.toString());
      }
    }
  }, [menuWidth]);
  
  // Gallery state management (moved from Gallery component to be shared between Header and Gallery)
  const [current, setCurrent] = useState<{ mode: GalleryMode | null; direction: number }>({ mode: "Ask Chat", direction: 1 }); // TODO: default to first mode instead of null when we are ready to bring it back in
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFirstAnimationRunning, setIsFirstAnimationRunning] = useState(false);
  const exitDirectionRef = useRef<number>(1);
  const setExitDirectionRef = useRef<((direction: number) => void) | null>(null);

  // Sync gallery mode with chat mode
  useEffect(() => {
    let newGalleryMode: GalleryMode;
    
    // Map chat mode and RAG chat type to gallery mode
    // Check for specific RAG types first (they can be active in either mode)
    if (chatModeState.ragChatType === 'Legal') {
      newGalleryMode = 'Legal';
    } else if (chatModeState.ragChatType === 'DevOps') {
      newGalleryMode = 'DevOps';
    } else if (chatModeState.ragChatType === 'TEBG') {
      newGalleryMode = 'TEBG';
    } else if (chatModeState.ragChatType === 'ITSM') {
      newGalleryMode = 'ITSM';
    } else if (chatModeState.ragChatType === 'JIRA') {
      newGalleryMode = 'JIRA';
    } else if (chatModeState.mode === 'Ask Chat') {
      newGalleryMode = 'Ask Chat';
    } else if (chatModeState.mode === '3M Knowledge') {
      // Default to 3M Knowledge for Everyday Insights or null
      newGalleryMode = '3M Knowledge';
    } else {
      // Fallback
      newGalleryMode = 'Ask Chat';
    }
    
    if (current.mode !== newGalleryMode) {
      // Update gallery mode without animation when chat mode changes
      setCurrent({ mode: newGalleryMode, direction: 1 });
    }
  }, [chatModeState.mode, chatModeState.ragChatType, current.mode]);

  // Function to change modes with direction for animation
  const goToModeWithDirection = useCallback((newMode: GalleryMode | null, direction: number) => {
    // Don't set exitDirectionRef here - it should be set by the calling component
    // before this function is called so the exit animation captures it correctly
    // Set animation state when changing modes
    setIsAnimating(true);
    setCurrent({ mode: newMode, direction: direction });
  }, []);

  // Navigate to chat page with a message
  const navigateToChat = useCallback((message: string, tools?: string[]) => {
    setChatMessage(message);
    setChatTools(tools || []);
    setCurrentPage('chat');
  }, []);

  // Navigate back to main page
  const navigateToMain = useCallback(() => {
    // Stop voice mode if active
    if (isVoiceModeActive) {
      logger.log('[App] Navigating to home - stopping voice mode');
      dispatch(cancelTranscribingMessages());
      voiceModeService.disconnect();
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false));
      dispatch(setVoiceModePaused(false));
      dispatch(setVoiceModeRestarting(false));
      dispatch(setShowInactivityWarning(false));
      dispatch(setInactivityCountdown(0));
      dispatch(setDisconnectedByInactivity(false));
    }
    
    setCurrentPage('main');
    setChatMessage('');
    setChatTools([]);
    // clear any uploaded files & analysis results so data_analysis starts fresh
    dispatch(clearUploadedFiles());
    dispatch(clearAnalysisResults());
    // Clear voice chat history for both modes
    dispatch(clearaskChatHistory());
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    // Always clear the selected RAG when returning home in Ask Chat mode
    if (chatModeState.mode === 'Ask Chat') {
      dispatch(setRagChatType(null));
    }
    // Clear auto-downloaded translation files tracking
    clearAutoDownloadedFiles();
    // Generate a new session ID when returning to home
    dispatch(resetSessionId());
  }, [dispatch, isVoiceModeActive, chatModeState.mode]);

  // Track whether the chat page has any messages (for Header confirmation dialog)
  const [hasChatMessages, setHasChatMessages] = useState(false);
  const handleHasMessagesChange = useCallback((hasMessages: boolean) => {
    setHasChatMessages(hasMessages);
  }, []);

  // Select global blocking overlay state
  const globalBlocking = useSelector((s: any) => s.loading.globalBlocking);
  // const blockingMessage = useSelector((s: any) => s.loading.blockingMessage); // unused currently

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <Header 
        mode={mode} 
        galleryState={{ current, goToModeWithDirection, exitDirectionRef, setExitDirectionRef, isFirstAnimationRunning }}
        onMenuToggle={() => setMobileMenuOpen(true)}
        isMobileOrTablet={isMobile || isTablet}
        currentPage={currentPage}
        onNavigateHome={navigateToMain}
        isStreaming={chatModeState.isStreaming}
        isVoiceModeActive={isVoiceModeActive}
        hasMessages={hasChatMessages}
      />
      
      {/* Mobile/Tablet Menu - Full Screen Overlay */}
      {(isMobile || isTablet) && (
        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          currentPage={currentPage}
          onClearChat={navigateToMain}
          isMobile={isMobile}
          onShowWhatsNew={handleShowWhatsNew}
        />
      )}

      {/* Mobile/Tablet Active RAG indicator bar — shown below header when a RAG is selected */}
      {(isMobile || isTablet) && chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType !== null && (() => {
        const ragDisplayConfig: Record<string, { label: string; icon: React.ReactNode }> = {
          'Everyday Insights': {
            label: 'Everyday Insights',
            icon: <svg width="16" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 3L2.5 3.00002C1.67157 3.00002 1 3.6716 1 4.50002V9.50003C1 10.3285 1.67157 11 2.5 11H7.50003C7.63264 11 7.75982 11.0527 7.85358 11.1465L10 13.2929V11.5C10 11.2239 10.2239 11 10.5 11H12.5C13.3284 11 14 10.3285 14 9.50003V4.5C14 3.67157 13.3284 3 12.5 3Z" fill="currentColor"/></svg>,
          },
          'Legal': {
            label: 'Legal',
            icon: <svg width="16" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 2.5C3 2.22386 3.22386 2 3.5 2H9.08579C9.21839 2 9.34557 2.05268 9.43934 2.14645L11.8536 4.56066C11.9473 4.65443 12 4.78161 12 4.91421V12.5C12 12.7761 11.7761 13 11.5 13H3.5C3.22386 13 3 12.7761 3 12.5V2.5ZM3.5 1C2.67157 1 2 1.67157 2 2.5V12.5C2 13.3284 2.67157 14 3.5 14H11.5C12.3284 14 13 13.3284 13 12.5V4.91421C13 4.51639 12.842 4.13486 12.5607 3.85355L10.1464 1.43934C9.86514 1.15804 9.48361 1 9.08579 1H3.5ZM4.5 7C4.22386 7 4 7.22386 4 7.5C4 7.77614 4.22386 8 4.5 8H10.5C10.7761 8 11 7.77614 11 7.5C11 7.22386 10.7761 7 10.5 7H4.5ZM4.5 9C4.22386 9 4 9.22386 4 9.5C4 9.77614 4.22386 10 4.5 10H7.5C7.77614 10 8 9.77614 8 9.5C8 9.22386 7.77614 9 7.5 9H4.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/></svg>,
          },
          'DevOps': {
            label: 'DevOps',
            icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
          },
        };
        const activeConfig = ragDisplayConfig[chatModeState.ragChatType as string];
        if (!activeConfig) return null;
        return (
          <div
            className="flex items-center justify-center gap-2 py-1.5 px-4 z-10"
            style={{
              backgroundColor: 'var(--main-background)',
              borderBottom: '1px solid var(--color-border, rgba(128,128,128,0.2))',
            }}
          >
            <span style={{ color: 'var(--color-text)', opacity: 0.9, display: 'flex', alignItems: 'center' }}>
              {activeConfig.icon}
            </span>
            <span
              className="text-xs font-semibold"
              style={{ fontFamily: '3MCircularTT', color: 'var(--color-text)', opacity: 0.9 }}
            >
              {activeConfig.label}
            </span>
          </div>
        );
      })()}
      
      <div className="flex flex-1 h-0 overflow-hidden">
        {/* Desktop Menu - Side Panel (hidden on mobile/tablet) */}
        {!isMobile && !isTablet && (
          <Menu 
            currentPage={currentPage}
            onClearChat={navigateToMain}
            width={menuWidth}
            onWidthChange={setMenuWidth}
            onShowWhatsNew={handleShowWhatsNew}
            deviceType="desktop"
          />
        )}
        {currentPage === 'main' && !shouldHideMainContent ? (
          <MainContent
            galleryState={{ current, goToModeWithDirection, exitDirectionRef, isAnimating, setIsAnimating, setExitDirectionRef, isFirstAnimationRunning, setIsFirstAnimationRunning }}
            onNavigateToChat={navigateToChat}
            deviceType={isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}
          />
        ) : currentPage === 'main' && shouldHideMainContent ? (
          <div className="flex-1 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg)' }}>
            <div className="text-center">
              {/* For now just throwing in messages so I don't have to worry about formatting on tiny screens */}
              <h2 className="text-xl font-semibold text-[var(--color-text)] mb-3">
                Screen Size Too Small
              </h2>
              <p 
                className="text-sm leading-relaxed max-w-md"
                style={{ color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}
              >
                This application requires a minimum screen width of 600px for the best experience. 
                Please use a larger screen or switch to a mobile device.
              </p>
            </div>
          </div>
        ) : (
          <ChatPage 
            initialMessage={chatMessage}
            initialTools={chatTools}
            onBackToMain={navigateToMain}
            onHasMessagesChange={handleHasMessagesChange}
          />
        )}
      </div>
      {/* Only show disclaimer on homepage and only for desktop */}
      {currentPage === 'main' && !isMobile && !isTablet && <DisclaimerNote menuWidth={menuWidth} />}
      
      {/* Image Modal — only for regular chat (Ask Chat / 3M Knowledge), not RAG modes */}
      {(!chatModeState.ragChatType) && <ImageModal />}
      
      {/* Jira Card Modal — only for JIRA RAG */}
      {chatModeState.ragChatType === 'JIRA' && <JiraCardModal />}

      {/* Tutorial Components */}
      <WelcomePopup 
        isOpen={showWelcomePopup}
        onStartTour={handleStartTutorial}
        onSkip={handleSkipTutorial}
        currentPage={currentPage}
      />
      
      <GuidedTutorial 
        isActive={isTutorialActive}
        currentStep={currentTutorialStep}
        currentStepIndex={currentStepIndex}
        totalSteps={steps.length}
        onNext={handleNextStep}
        onPrevious={handlePreviousStep}
        onExit={handleExitTutorial}
      />

      {/* Spinner for when the file upload pop up is active */}
      {globalBlocking && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
            <div style={{ width: 42, height: 42, border: '5px solid rgba(255,255,255,0.25)', borderTop: '5px solid var(--color-accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      )}
    </div>
  );
}

export default App;